import { useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { alpha, useTheme } from '@rossum/ui/material';
import { createStoreHooks, type FeatureDeps } from '../../../core';
import { createPanFeature } from '../../pan/feature';
import {
  MIN_THUMB_SIZE,
  THUMB_BORDER_RADIUS,
  THUMB_INSET,
  THUMB_THICKNESS,
  TRACK_SIZE,
} from './scrollbar-constants';
import { useScrollbarDrag } from './useScrollbarDrag';

type ScrollbarsDeps = FeatureDeps<[typeof createPanFeature]>;

const { useStore: useSelector } = createStoreHooks<ScrollbarsDeps>();

type ScrollbarMetrics = {
  visible: boolean;
  thumbSize: number;
  thumbPosition: number;
  maxScroll: number;
};

const calculateScrollbarMetrics = (
  contentSize: number,
  viewportSize: number,
  translate: number,
  zoomLevel: number,
  trackSize: number,
  padding: number
): ScrollbarMetrics => {
  const paddingInCanvasUnits = padding / zoomLevel;
  const totalSize = contentSize + paddingInCanvasUnits;
  const scaledTotalSize = totalSize * zoomLevel;
  const visible = scaledTotalSize > viewportSize;

  if (!visible) {
    return { visible: false, thumbSize: 0, thumbPosition: 0, maxScroll: 0 };
  }

  const thumbSize = Math.max(
    MIN_THUMB_SIZE,
    (viewportSize / scaledTotalSize) * trackSize
  );

  const scaledViewportSize = viewportSize / zoomLevel;
  const availableSpace = (totalSize - scaledViewportSize) * zoomLevel;
  const maxTranslate = availableSpace;

  const thumbPosition = (-translate / maxTranslate) * (trackSize - thumbSize);

  return {
    visible: true,
    thumbSize,
    thumbPosition: Math.max(0, Math.min(thumbPosition, trackSize - thumbSize)),
    maxScroll: maxTranslate,
  };
};

export const Scrollbars = () => {
  const theme = useTheme();
  const colors = useMemo(
    () => ({
      track: alpha(
        theme.palette.action.hover,
        theme.palette.action.hoverOpacity
      ),
      thumb: alpha(theme.palette.action.active, 0.4),
      thumbActive: alpha(theme.palette.action.active, 0.6),
    }),
    [theme.palette.action]
  );

  const actions = useSelector(s => s.pan?.actions);
  const canvasTransform = useSelector(s => s.canvas.transform);
  const viewportRect = useSelector(s => s.canvas.viewportRect);
  const canvasDimensions = useSelector(s => s.canvas.dimensions);
  const padding = useSelector(s => s.canvas.padding);
  const overlayRef = useSelector(s => s.canvas.refs.overlayRef);

  const horizontalTrackRef = useRef<HTMLDivElement>(null);
  const verticalTrackRef = useRef<HTMLDivElement>(null);

  const needsHorizontal =
    canvasDimensions.width * canvasTransform.zoom +
      padding.left +
      padding.right >
    viewportRect.width;
  const needsVertical =
    canvasDimensions.height * canvasTransform.zoom +
      padding.top +
      padding.bottom >
    viewportRect.height;

  const horizontalMetrics = calculateScrollbarMetrics(
    canvasDimensions.width,
    viewportRect.width,
    canvasTransform.translateX,
    canvasTransform.zoom,
    viewportRect.width - (needsVertical ? TRACK_SIZE : 0),
    padding.left + padding.right
  );

  const verticalMetrics = calculateScrollbarMetrics(
    canvasDimensions.height,
    viewportRect.height,
    canvasTransform.translateY,
    canvasTransform.zoom,
    viewportRect.height - (needsHorizontal ? TRACK_SIZE : 0),
    padding.top + padding.bottom
  );

  const { horizontal, vertical } = useScrollbarDrag(
    actions?.panTo,
    horizontalMetrics,
    verticalMetrics,
    horizontalTrackRef,
    verticalTrackRef
  );

  if (!overlayRef.current) {
    return null;
  }

  return createPortal(
    <foreignObject
      x={0}
      y={0}
      width={viewportRect.width}
      height={viewportRect.height}
      style={{ pointerEvents: 'none' }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          pointerEvents: 'none',
        }}
      >
        {horizontalMetrics.visible && (
          <div
            ref={horizontalTrackRef}
            onClick={horizontal.onTrackClick}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: verticalMetrics.visible ? TRACK_SIZE : 0,
              height: TRACK_SIZE,
              backgroundColor: colors.track,
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            <div
              onMouseDown={horizontal.onThumbMouseDown}
              style={{
                position: 'absolute',
                left: horizontalMetrics.thumbPosition,
                top: THUMB_INSET,
                width: horizontalMetrics.thumbSize,
                height: THUMB_THICKNESS,
                backgroundColor: horizontal.isDragging
                  ? colors.thumbActive
                  : colors.thumb,
                borderRadius: THUMB_BORDER_RADIUS,
                cursor: 'grab',
                transition: horizontal.isDragging
                  ? 'none'
                  : 'background-color 0.2s',
                pointerEvents: 'auto',
              }}
            />
          </div>
        )}

        {verticalMetrics.visible && (
          <div
            ref={verticalTrackRef}
            onClick={vertical.onTrackClick}
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: horizontalMetrics.visible ? TRACK_SIZE : 0,
              width: TRACK_SIZE,
              backgroundColor: colors.track,
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            <div
              onMouseDown={vertical.onThumbMouseDown}
              style={{
                position: 'absolute',
                top: verticalMetrics.thumbPosition,
                left: THUMB_INSET,
                width: THUMB_THICKNESS,
                height: verticalMetrics.thumbSize,
                backgroundColor: vertical.isDragging
                  ? colors.thumbActive
                  : colors.thumb,
                borderRadius: THUMB_BORDER_RADIUS,
                cursor: 'grab',
                transition: vertical.isDragging
                  ? 'none'
                  : 'background-color 0.2s',
              }}
            />
          </div>
        )}

        {horizontalMetrics.visible && verticalMetrics.visible && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: TRACK_SIZE,
              height: TRACK_SIZE,
              backgroundColor: colors.track,
              pointerEvents: 'auto',
            }}
          />
        )}
      </div>
    </foreignObject>,
    overlayRef.current
  );
};
